import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { getMyNotifications, markMyNotificationsRead } from '../../services/api';

const REFRESH_MS = 60000;

const timeAgo = (value) => {
  try {
    const date = new Date(value);
    // Une horloge d'appareil en retard ne doit pas afficher « dans 1 heure ».
    if (date.getTime() > Date.now() - 60000) return "à l'instant";
    return formatDistanceToNow(date, { addSuffix: true, locale: fr });
  } catch (error) {
    return '';
  }
};

export const NotificationBell = ({ buttonClassName = 'p-2 rounded-full hover:bg-slate-100 relative focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00b4d8]' }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const response = await getMyNotifications();
      setItems(response.data?.items || []);
      setUnreadCount(response.data?.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(timer);
  }, [refresh]);

  const markRead = async (ids) => {
    const targets = ids || items.filter(item => !item.read).map(item => item.id);
    if (targets.length === 0) return;
    setItems(current => current.map(item => (targets.includes(item.id) ? { ...item, read: true } : item)));
    setUnreadCount(current => (ids ? Math.max(0, current - targets.length) : 0));
    try {
      await markMyNotificationsRead(ids || []);
    } catch (error) {
      console.error('Error marking notifications read:', error);
      refresh();
    }
  };

  const openNotification = (item) => {
    if (!item.read) markRead([item.id]);
    if (item.link) navigate(item.link);
  };

  return (
    <DropdownMenu onOpenChange={(open) => open && refresh()}>
      <DropdownMenuTrigger asChild>
        <button
          className={buttonClassName}
          data-testid="notifications-btn"
          aria-label={unreadCount ? `Notifications, ${unreadCount} non lue${unreadCount > 1 ? 's' : ''}` : 'Notifications'}
        >
          <Bell className="w-5 h-5 text-slate-600" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-none border-2 border-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-w-[calc(100vw-2rem)]">
        <div className="p-3 border-b flex items-center justify-between gap-2">
          <h3 className="font-semibold text-[#1a3a5c]">Notifications</h3>
          {unreadCount > 0 && (
            <button
              onClick={(event) => {
                event.preventDefault();
                markRead();
              }}
              className="text-xs text-[#00b4d8] hover:text-[#009bb8] font-medium"
              data-testid="notifications-read-all"
            >
              Tout marquer comme lu
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-4 text-sm text-slate-400 text-center">Aucune notification</div>
          ) : (
            items.map(item => (
              <DropdownMenuItem
                key={item.id}
                onClick={() => openNotification(item)}
                className={`flex items-start gap-3 p-3 cursor-pointer ${item.read ? '' : 'bg-[#00b4d8]/5'}`}
                data-testid={`notification-${item.id}`}
              >
                <span
                  className={`mt-1.5 w-2 h-2 shrink-0 rounded-full ${item.read ? 'bg-transparent' : 'bg-red-500'}`}
                  aria-hidden="true"
                />
                <span className="flex flex-col gap-0.5 min-w-0">
                  <span className={`text-sm ${item.read ? 'text-slate-600' : 'font-medium text-[#1a3a5c]'}`}>
                    {item.title}
                  </span>
                  <span className="text-xs text-slate-500 break-words">{item.message}</span>
                  <span className="text-[11px] text-slate-400">{timeAgo(item.created_at)}</span>
                </span>
              </DropdownMenuItem>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
